import { parseISO } from 'date-fns';

import { CalendarDay } from '../api/types';

import ShiftCard from './ShiftCard';

// One weekday column: header (weekday label + fill-rate% band + date number)
// then the stacked shift cards. Today's column is highlighted green and its
// weekday label reads "Today". Fill rate renders "–" when there are no shifts.

const fillBand = (pct: number | null | undefined): string => {
  if (pct == null) return 'band-none';
  if (pct >= 100) return 'band-high';
  if (pct >= 50) return 'band-mid';
  return 'band-low';
};

type Props = {
  day: CalendarDay;
  onSelectShift?: (shiftId: number) => void;
  /** This viewer has Smart Fill; see ShiftCard. */
  smartFill?: boolean;
  /** A filter or reason chip is on: an empty day may still have shifts. */
  filtered?: boolean;
};

const DayColumn = ({ day, onSelectShift, smartFill, filtered }: Props) => {
  const dayNum = parseISO(day.date).getDate();
  const weekdayLabel = day.is_today ? 'Today' : day.weekday;

  return (
    // `data-date` is the column's own anchor: the week grid is seven identical
    // columns, so a spec asserting on ONE day's cards (GRAVY-10341 compares the
    // order of a day against the Day view) would otherwise have to count
    // columns and hope the offset is right.
    <div
      className={`dashv2-daycol${day.is_today ? ' is-today' : ''}`}
      data-date={day.date}
    >
      <div className="dashv2-dayhead">
        <div className="dashv2-dayhead-top">
          <span className="dashv2-weekday">{weekdayLabel}</span>
          <span className={`dashv2-fillpct ${fillBand(day.fill_rate_pct)}`}>
            {day.fill_rate_pct == null ? '–' : `${day.fill_rate_pct}%`}
          </span>
        </div>
        <span className="dashv2-daynum">{dayNum}</span>
      </div>

      <div className="dashv2-shiftlist">
        {day.shifts.length === 0 ? (
          // A filter that empties the day is not an empty day (review D8-03,
          // as D7-02 for the Day view; short for the narrow column).
          <div className="dashv2-empty">
            {filtered ? 'No shifts match' : 'No shifts'}
          </div>
        ) : (
          day.shifts.map((card) => (
            <ShiftCard
              key={card.id}
              card={card}
              onSelect={onSelectShift}
              smartFill={smartFill}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default DayColumn;
